import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Tooltip from '@mui/material/Tooltip';

import { useGetObsEventsQuery } from '../../redux/services/obs-api';
import { ObsError } from './ObsError';

const LEVEL_COLOR = {
  fatal: 'error',
  error: 'error',
  warn: 'warning',
  info: 'info',
  debug: 'default',
};

const fmtDate = value => (value ? new Date(value).toLocaleString() : '—');

function EventRow({ event, activeInstallId, onToggleInstallFilter }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const verified = event.actor?.verified === true;
  const isFilteredInstall = !!event.installId && event.installId === activeInstallId;

  const copyJson = async () => {
    const text = JSON.stringify(event, null, 2);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API no disponible (contexto no seguro): fallback con textarea.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(v => !v)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {fmtDate(event.receivedAt)}
        </TableCell>
        <TableCell>
          <Chip size="small" color={LEVEL_COLOR[event.level] || 'default'} label={event.level} />
        </TableCell>
        <TableCell>{event.category}</TableCell>
        <TableCell
          title={event.event}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.event}
        </TableCell>
        <TableCell sx={{ overflow: 'hidden' }}>
          {event.actor?.claimedUsername || event.actor?.claimedUserId || '—'}{' '}
          {/* La falta de garantía se ve en pantalla, no solo en la documentación */}
          {event.actor && !verified && (
            <Chip size="small" variant="outlined" color="warning" label="no verificada" />
          )}
          {verified && <Chip size="small" color="success" label="verificada" />}
        </TableCell>
        <TableCell>{event.source?.platform || '—'}</TableCell>
        <TableCell>
          {event.installId ? (
            <Tooltip
              title={
                isFilteredInstall
                  ? 'Quitar el filtro de este dispositivo'
                  : `Ver todo lo que hizo este dispositivo (${event.installId})`
              }
            >
              <Chip
                size="small"
                clickable
                variant={isFilteredInstall ? 'filled' : 'outlined'}
                color={isFilteredInstall ? 'primary' : 'default'}
                label={`${event.installId.slice(0, 8)}…`}
                onClick={() => onToggleInstallFilter(event.installId)}
                sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
              />
            </Tooltip>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell
          title={event.message}
          sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {event.message}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ position: 'relative' }}>
              <Tooltip title={copied ? 'Copiado' : 'Copiar JSON'} placement="left">
                <IconButton
                  size="small"
                  onClick={copyJson}
                  aria-label="Copiar JSON del evento"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: 'background.paper' },
                  }}
                >
                  {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {/* Solo nodos de texto: el contenido viene de clientes no confiables.
                  pre-wrap + overflowWrap: una linea larga (stack, mensaje sin
                  espacios) envuelve dentro del ancho de la tabla en vez de
                  ensancharla; el alto se acota con scroll vertical. */}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  fontSize: '0.75rem',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  maxWidth: '100%',
                  maxHeight: 360,
                  overflowY: 'auto',
                }}
              >
                {JSON.stringify(event, null, 2)}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const EMPTY_FILTERS = {
  level: '',
  category: '',
  platform: '',
  event: '',
  installId: '',
  actor: '',
  statusCode: '',
  q: '',
  from: '',
  to: '',
};

function StreamTab() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data, isLoading, isFetching, error } = useGetObsEventsQuery(
    { ...filters, page: page + 1, limit },
    { pollingInterval: autoRefresh ? 10000 : 0 }
  );

  const setFilter = key => e => {
    setFilters(f => ({ ...f, [key]: e.target.value }));
    setPage(0);
  };

  // Un click en el chip de un dispositivo filtra el stream a su historia
  // completa (la pelicula ordenada por seq); otro click lo quita.
  const toggleInstallFilter = installId => {
    setFilters(f => ({ ...f, installId: f.installId === installId ? '' : installId }));
    setPage(0);
  };

  const selectSx = { minWidth: 120 };

  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField select size="small" label="Nivel" value={filters.level} onChange={setFilter('level')} sx={selectSx}>
          <MenuItem value="">Todos</MenuItem>
          {['fatal', 'error', 'warn', 'info', 'debug'].map(l => (
            <MenuItem key={l} value={l}>{l}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Categoría" value={filters.category} onChange={setFilter('category')} sx={selectSx}>
          <MenuItem value="">Todas</MenuItem>
          {['crash', 'action', 'net', 'sync', 'system'].map(c => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Plataforma" value={filters.platform} onChange={setFilter('platform')} sx={selectSx}>
          <MenuItem value="">Todas</MenuItem>
          {['ios', 'android', 'web', 'server'].map(p => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Evento" value={filters.event} onChange={setFilter('event')} />
        <TextField size="small" label="Install ID" value={filters.installId} onChange={setFilter('installId')} />
        <TextField size="small" label="Actor" value={filters.actor} onChange={setFilter('actor')} />
        <TextField size="small" label="Status" value={filters.statusCode} onChange={setFilter('statusCode')} sx={{ width: 90 }} />
        <TextField size="small" label="Buscar" value={filters.q} onChange={setFilter('q')} />
        <TextField
          size="small"
          label="Desde"
          type="datetime-local"
          value={filters.from}
          onChange={setFilter('from')}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Hasta"
          type="datetime-local"
          value={filters.to}
          onChange={setFilter('to')}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />}
          label={`Auto-refresh 10 s${isFetching ? ' · …' : ''}`}
        />
      </Paper>

      {isLoading && <CircularProgress sx={{ m: 4 }} />}
      {error && <ObsError error={error} context="los eventos" />}

      {data && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          {/* Layout fijo: la tabla nunca excede a su contenedor; el texto largo
              hace elipsis en su celda en vez de empujar el ancho. En pantallas
              estrechas el minWidth mantiene legible y el contenedor scrollea. */}
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 44 }} />
                <TableCell sx={{ width: 165 }}>Recibido</TableCell>
                <TableCell sx={{ width: 84 }}>Nivel</TableCell>
                <TableCell sx={{ width: 90 }}>Categoría</TableCell>
                <TableCell sx={{ width: 150 }}>Evento</TableCell>
                <TableCell sx={{ width: 170 }}>Actor</TableCell>
                <TableCell sx={{ width: 85 }}>Plataforma</TableCell>
                <TableCell sx={{ width: 110 }}>Install</TableCell>
                <TableCell>Mensaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map(ev => (
                <EventRow
                  key={ev._id}
                  event={ev}
                  activeInstallId={filters.installId}
                  onToggleInstallFilter={toggleInstallFilter}
                />
              ))}
              {data.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={{ p: 2 }} color="text.secondary">
                      Sin eventos con estos filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.pagination?.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </TableContainer>
      )}
    </Box>
  );
}

export default StreamTab;
